import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Check, Lock, Pencil, Trash2, Unlock, X } from 'lucide-react';

import { cn } from '@/lib/cn';
import { Combobox } from '@/components/Combobox';
import { useWines } from '@/features/wines/useWines';
import {
  useBox, useConfirmBox, useDeleteBox, useReopenBox, useSetSlot, useUpdateBox,
  type BoxSlot,
} from './useBoxes';

export function BoxEditorPage() {
  const navigate = useNavigate();
  const params = useParams();
  const boxId = Number(params.id);
  const { data: box } = useBox(boxId);
  const { data: winesData } = useWines({ per_page: 500 });
  const setSlot = useSetSlot();
  const confirmBox = useConfirmBox();
  const reopenBox = useReopenBox();
  const updateBox = useUpdateBox();
  const deleteBox = useDeleteBox();

  const [picking, setPicking] = useState<{ row: number; col: number } | null>(null);
  const [pickWineId, setPickWineId] = useState<number | null>(null);
  const [showEditDims, setShowEditDims] = useState(false);

  // IMPORTANTE: todos los hooks deben ejecutarse antes del return condicional,
  // si no React tira el error #310 (cambia el número de hooks entre renders).
  const slotMap = useMemo(() => {
    const m = new Map<string, BoxSlot>();
    box?.slots?.forEach((s) => m.set(`${s.row}-${s.col}`, s));
    return m;
  }, [box?.slots]);

  if (!box) return <div className="p-4 md:p-8 text-notion-500">Cargando caja…</div>;

  // Filas de arriba hacia abajo (row=rows arriba, row=1 abajo).
  const rowsTopDown = Array.from({ length: box.rows }, (_, i) => box.rows - i);
  const colsLeftRight = Array.from({ length: box.cols }, (_, i) => i + 1);

  const isConfirmed = box.status === 'confirmed';

  const openPicker = (row: number, col: number) => {
    if (isConfirmed) return;
    setPicking({ row, col });
    const existing = slotMap.get(`${row}-${col}`);
    setPickWineId(existing?.wine_id ?? null);
  };

  const assignWine = async () => {
    if (!picking || !pickWineId) return;
    await setSlot.mutateAsync({
      boxId, row: picking.row, col: picking.col,
      wine_id: pickWineId, status: 'planned',
    });
    setPicking(null);
    setPickWineId(null);
  };

  const clearSlot = async (row: number, col: number) => {
    await setSlot.mutateAsync({ boxId, row, col, wine_id: null, status: 'empty' });
  };

  const toggleConfirm = async (slot: BoxSlot) => {
    if (!slot.wine_id) return;
    const newStatus: BoxSlot['status'] = slot.status === 'confirmed' ? 'planned' : 'confirmed';
    await setSlot.mutateAsync({
      boxId, row: slot.row, col: slot.col,
      wine_id: slot.wine_id, status: newStatus,
    });
  };

  const closeBox = async () => {
    if (!confirm('¿Cerrar la caja? Los slots planificados con vino quedan como confirmados.')) return;
    try {
      await confirmBox.mutateAsync(boxId);
    } catch (err: any) {
      const resp = err?.response?.data;
      if (resp?.error === 'over_allocated' && Array.isArray(resp.over_allocated)) {
        const lines = resp.over_allocated
          .map((o: any) => `• ${o.wine_name}: stock ${o.stock}, asignadas ${o.allocated_total} (acá ${o.allocated_here}, otras cajas ${o.allocated_other})`)
          .join('\n');
        alert(`${resp.message}\n\n${lines}\n\nLiberá slots de esta caja o de otras antes de cerrar.`);
      } else {
        alert(resp?.message || 'No se pudo cerrar la caja.');
      }
    }
  };

  const reopen = async () => {
    if (!confirm('¿Volver a abrir la caja para editar?')) return;
    await reopenBox.mutateAsync(boxId);
  };

  const removeBox = async () => {
    if (!confirm(`¿Eliminar la caja ${box.box_number}? Los vinos asignados quedarán liberados (no se borran del stock).`)) return;
    await deleteBox.mutateAsync(boxId);
    navigate('/boxes');
  };

  const saveEdits = async (next: { boxNumber: string; rows: number; cols: number }) => {
    try {
      await updateBox.mutateAsync({
        id: boxId,
        payload: {
          box_number: next.boxNumber,
          rows: next.rows,
          cols: next.cols,
        },
      });
      setShowEditDims(false);
    } catch (err: any) {
      const msg = err?.response?.data?.message
        ?? 'No se pudieron guardar los cambios.';
      alert(msg);
    }
  };

  const total = box.rows * box.cols;
  const confirmedCount = box.slots?.filter((s) => s.status === 'confirmed').length ?? 0;
  const plannedCount = box.slots?.filter((s) => s.status === 'planned').length ?? 0;

  return (
    <div className="p-4 md:p-8 max-w-3xl">
      <button
        type="button"
        onClick={() => navigate('/boxes')}
        className="text-sm text-notion-500 inline-flex items-center gap-1 mb-3"
      >
        <ArrowLeft size={14} /> Volver a Cajas
      </button>

      <header className="mb-4 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl md:text-2xl font-medium">Caja {box.box_number}</h1>
          <p className="text-sm text-notion-500">
            {box.rows}×{box.cols} ({total} posiciones) ·{' '}
            <strong className="text-notion-800">{confirmedCount}</strong> confirmadas ·{' '}
            <strong className="text-notion-800">{plannedCount}</strong> planificadas
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!isConfirmed && (
            <button
              type="button"
              onClick={() => setShowEditDims(true)}
              className="btn-secondary"
              title="Editar número, filas y columnas"
            >
              <Pencil size={14} /> Editar
            </button>
          )}
          <button
            type="button"
            onClick={removeBox}
            disabled={deleteBox.isPending}
            className="btn-secondary text-rose-700 border-rose-200 hover:bg-rose-50"
            title="Eliminar caja"
          >
            <Trash2 size={14} /> Eliminar
          </button>
          {isConfirmed ? (
            <button type="button" onClick={reopen} className="btn-secondary">
              <Unlock size={14} /> Reabrir
            </button>
          ) : (
            <button
              type="button"
              onClick={closeBox}
              disabled={plannedCount === 0 && confirmedCount === 0}
              className="btn-primary bg-emerald-600 hover:bg-emerald-700"
            >
              <Lock size={14} /> Cerrar caja
            </button>
          )}
        </div>
      </header>

      {isConfirmed && (
        <div className="card p-3 mb-3 bg-emerald-50 border-emerald-200 text-sm text-emerald-900">
          Caja cerrada. Para editarla, tocá <strong>Reabrir</strong>.
        </div>
      )}

      {/* Grilla principal */}
      <div className="card p-3 md:p-4">
        <div
          className="grid gap-2 justify-start"
          style={{
            // Tope de ancho por celda = el que tendría una caja de 3 columnas,
            // así una 1×1 o 1×2 no se agranda; con más columnas la celda se
            // achica sola para entrar (minmax permite bajar de 240px).
            gridTemplateColumns: `repeat(${box.cols}, minmax(0, 240px))`,
          }}
        >
          {rowsTopDown.flatMap((row) =>
            colsLeftRight.map((col) => {
              const slot = slotMap.get(`${row}-${col}`);
              const status = slot?.status ?? 'empty';
              const wine = slot?.wine;
              const hasImage = !!wine?.image_url;
              return (
                <div
                  key={`${row}-${col}`}
                  className={cn(
                    'relative aspect-square rounded-lg border-2 overflow-hidden text-[10px] transition',
                    status === 'confirmed' && 'border-emerald-700',
                    status === 'planned' && 'border-amber-300',
                    status === 'empty' && 'border-notion-200 border-dashed hover:border-notion-400',
                    // Fondo sólido solo cuando NO hay foto (si hay, la foto es el fondo).
                    !hasImage && status === 'confirmed' && 'bg-emerald-600 text-white',
                    !hasImage && status === 'planned' && 'bg-amber-50 text-amber-900',
                    !hasImage && status === 'empty' && 'bg-notion-50 text-notion-400',
                    hasImage && 'text-white',
                  )}
                >
                  {hasImage && (
                    <>
                      <img
                        src={wine!.image_url!}
                        alt=""
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      {/* Gradiente para legibilidad del nombre sobre la foto. */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-black/25" />
                    </>
                  )}

                  <div className="relative z-10 h-full p-1.5 flex flex-col justify-between">
                    <div className="flex items-center justify-between text-[9px]">
                      <span className={cn(hasImage ? 'bg-black/50 rounded px-1 py-0.5' : 'opacity-70')}>
                        F{row}·C{col}
                      </span>
                      {!isConfirmed && slot?.wine_id && (
                        <button
                          type="button"
                          onClick={() => clearSlot(row, col)}
                          className={cn(
                            'hover:opacity-100',
                            hasImage ? 'bg-black/50 rounded p-0.5 opacity-80' : 'opacity-60',
                          )}
                          aria-label="Limpiar slot"
                        >
                          <X size={9} />
                        </button>
                      )}
                    </div>

                    {wine ? (
                      <button
                        type="button"
                        onClick={() => !isConfirmed && openPicker(row, col)}
                        className="text-left overflow-hidden"
                        title={`${wine.name} ${wine.vintage ?? ''}`}
                      >
                        <span
                          className={cn(
                            'block',
                            hasImage && 'bg-black/55 rounded px-1 py-0.5',
                          )}
                        >
                          <span className="block truncate text-[11px] leading-tight font-medium">
                            {wine.name}
                          </span>
                          <span className={cn('block text-[9px] truncate', !hasImage && 'opacity-70')}>
                            {wine.winery ?? ''} {wine.vintage ?? ''}
                          </span>
                        </span>
                      </button>
                    ) : (
                      <button
                        type="button"
                        disabled={isConfirmed}
                        onClick={() => openPicker(row, col)}
                        className="text-[10px] underline opacity-70 self-start"
                      >
                        asignar vino
                      </button>
                    )}

                    {slot?.wine_id && !isConfirmed && (
                      <button
                        type="button"
                        onClick={() => toggleConfirm(slot)}
                        className={cn(
                          'text-[9px] py-0.5 rounded inline-flex items-center justify-center gap-1',
                          status === 'confirmed'
                            ? 'bg-white/25 text-white'
                            : 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200',
                        )}
                      >
                        <Check size={9} />
                        {status === 'confirmed' ? 'confirmado' : 'validar'}
                      </button>
                    )}
                  </div>
                </div>
              );
            }),
          )}
        </div>
        <p className="text-[10px] text-notion-400 mt-3 text-center">
          ← cols 1..{box.cols} →   ·   filas 1 abajo, {box.rows} arriba
        </p>
      </div>

      {/* Modal de editar caja (número + dimensiones) */}
      {showEditDims && (
        <EditBoxModal
          currentBoxNumber={box.box_number}
          currentRows={box.rows}
          currentCols={box.cols}
          onCancel={() => setShowEditDims(false)}
          onSave={saveEdits}
          submitting={updateBox.isPending}
        />
      )}

      {/* Modal de asignar vino */}
      {picking && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center justify-center p-4"
          onClick={() => setPicking(null)}
        >
          <div
            className="bg-white rounded-2xl p-4 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="font-medium text-notion-800 mb-2">
              Asignar vino a la posición F{picking.row}·C{picking.col}
            </p>
            <Combobox
              value={pickWineId}
              placeholder="Buscar por nombre, bodega…"
              options={(() => {
                // Vino que ya estaba en este slot (si lo había): se incluye
                // siempre como opción para poder mantenerlo.
                const currentWineId =
                  slotMap.get(`${picking.row}-${picking.col}`)?.wine_id ?? null;

                return (winesData?.data ?? [])
                  .filter((w) => {
                    if (w.id === currentWineId) return true;
                    const stock = w.current_stock ?? 0;
                    const inBoxes = w.bottles_in_boxes ?? 0;
                    return stock - inBoxes > 0;
                  })
                  .map((w) => {
                    const stock = w.current_stock ?? 0;
                    const inBoxes = w.bottles_in_boxes ?? 0;
                    const available =
                      w.id === currentWineId
                        ? stock - inBoxes + 1
                        : stock - inBoxes;
                    return {
                      id: w.id,
                      label: `${w.name}${w.vintage ? ` ${w.vintage}` : ''} · ${w.format}`,
                      hint: `${w.winery?.name ?? ''} · ${available} disp.`,
                    };
                  });
              })()}
              onChange={(id) => setPickWineId(id ? Number(id) : null)}
            />
            <div className="flex gap-2 mt-3">
              <button type="button" className="btn-secondary flex-1" onClick={() => setPicking(null)}>
                Cancelar
              </button>
              <button
                type="button"
                className="btn-primary flex-1"
                onClick={assignWine}
                disabled={!pickWineId || setSlot.isPending}
              >
                Asignar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Modal para editar la caja: número identificador y dimensiones (filas/cols).
 * Si las nuevas dimensiones son menores y hay slots fuera de rango con vino,
 * el backend devuelve 422 y mostramos el mensaje.
 */
function EditBoxModal({
  currentBoxNumber,
  currentRows,
  currentCols,
  onCancel,
  onSave,
  submitting,
}: {
  currentBoxNumber: string;
  currentRows: number;
  currentCols: number;
  onCancel: () => void;
  onSave: (next: { boxNumber: string; rows: number; cols: number }) => void;
  submitting?: boolean;
}) {
  const [boxNumber, setBoxNumber] = useState(currentBoxNumber);
  const [rows, setRows] = useState(currentRows);
  const [cols, setCols] = useState(currentCols);
  const trimmed = boxNumber.trim();
  const changed =
    trimmed !== currentBoxNumber || rows !== currentRows || cols !== currentCols;
  const valid = trimmed.length > 0;

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center justify-center p-4"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-2xl p-4 max-w-sm w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="font-medium text-notion-800 mb-1">Editar caja</p>
        <p className="text-xs text-notion-500 mb-3">
          Si achicás las dimensiones, los slots fuera de rango deben estar vacíos.
        </p>

        <label className="label">Número de caja</label>
        <input
          type="text"
          value={boxNumber}
          onChange={(e) => setBoxNumber(e.target.value)}
          maxLength={60}
          className="input mb-3"
          placeholder="Ej: 12"
        />

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="label">Filas</label>
            <input
              type="number"
              min={1}
              max={20}
              value={rows}
              onChange={(e) => setRows(Math.max(1, Math.min(20, Number(e.target.value) || 1)))}
              className="input text-center"
            />
          </div>
          <div>
            <label className="label">Columnas</label>
            <input
              type="number"
              min={1}
              max={20}
              value={cols}
              onChange={(e) => setCols(Math.max(1, Math.min(20, Number(e.target.value) || 1)))}
              className="input text-center"
            />
          </div>
        </div>
        <p className="text-xs text-notion-500 mb-3">
          Total: <strong>{rows * cols}</strong> posiciones (antes {currentRows * currentCols}).
        </p>

        <div className="flex gap-2">
          <button type="button" onClick={onCancel} className="btn-secondary flex-1">
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => onSave({ boxNumber: trimmed, rows, cols })}
            disabled={!changed || !valid || submitting}
            className="btn-primary flex-1"
          >
            {submitting ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}
